'use client'

import {
    type AllHTMLAttributes,
    type CSSProperties,
    type MouseEvent,
    useEffect,
    useRef,
    useState,
} from 'react'

import cn from 'clsx'
import { Button } from '@/components/Button/Button'

import css from './GroupButton.module.css'

type ButtonState = {
    name: string
    value: string
    active?: boolean
}

interface ActiveElementCSSProperties extends CSSProperties {
    '--offset': string
    '--width': string
    '--display': string
}

interface ListCSSProperties extends CSSProperties {
    '--count': number
}

export interface GroupButtonProps extends Omit<AllHTMLAttributes<HTMLUListElement>, 'onClick'> {
    buttons: ButtonState[]
    onClick?: (name: string) => void
    className?: string
}

export function GroupButton({ buttons, className, onClick }: GroupButtonProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [buttonState, setButtonState] = useState<ButtonState[]>(buttons)
    const [dimensions, setDimensions] = useState({ width: 0, offset: 0, display: 'none' })

    const activeElementProps: ActiveElementCSSProperties = {
        '--offset': `${dimensions.offset}px`,
        '--width': `${dimensions.width}px`,
        '--display': `${dimensions.display}`,
    }

    const listProps: ListCSSProperties = {
        '--count': buttons.length,
    }

    useEffect(() => {
        setButtonState(buttons)
    }, [buttons])

    useEffect(() => {
        if (ref.current) {
            const dimensions = activeButtonDimensions(ref.current, buttonState)
            if (dimensions) {
                setDimensions(dimensions)
            }
        }
    }, [buttonState])

    useEffect(() => {
        let resizeObserver: ResizeObserver
        const el = ref.current
        if (el && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                const dimensions = activeButtonDimensions(el, buttonState)
                if (dimensions) {
                    setDimensions(dimensions)
                }
            })

            resizeObserver.observe(el)
        }

        return function resizeObserverCleanup() {
            if (resizeObserver) {
                resizeObserver.disconnect()
            }
        }
    }, [buttonState])

    function handleOnClick(event: MouseEvent<HTMLElement>, value: string) {
        if (onClick) {
            onClick(value)
        }

        if (event.currentTarget) {
            const buttonStateCopy = [...buttonState].map((state) => {
                state.active = state.value === value
                return state
            })
            setButtonState(buttonStateCopy)
        }
    }

    return (
        <div className={cn(css.container, className)}>
            <span className={css.activeBackground} style={activeElementProps} />
            <div ref={ref} role="region" style={listProps} className={css.list}>
                {buttonState.map(({ name, active, value }) => (
                    <Button
                        key={name}
                        id={`groupButton-${value}`}
                        className={cn(css.groupButton, { [css.active]: active })}
                        onClick={(evt) => handleOnClick(evt, value)}
                        variant="quaternary"
                        aria-pressed={active}
                    >
                        {name}
                    </Button>
                ))}
            </div>
        </div>
    )
}

function activeButtonDimensions(el: HTMLDivElement, buttonState: ButtonState[]) {
    const activeButtonName = buttonState.find((state) => state.active)
    const activeButtonEl = el.querySelector(
        `#groupButton-${activeButtonName?.value}`,
    ) as HTMLElement | null

    if (activeButtonEl) {
        /**
         * We're using computed style to get element width without applied
         * transforms and as a decimal value, so we can cut decimal value
         * instead of rounding up/down as offsetWidth does.
         */
        const width = Number.parseInt(window.getComputedStyle(activeButtonEl).width)
        const offset = activeButtonEl.offsetLeft

        return {
            offset,
            width,
            display: 'block',
        }
    }
}
